import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CreditCard } from "@phosphor-icons/react/CreditCard";
import { HardDrives } from "@phosphor-icons/react/HardDrives";
import { MapTrifold } from "@phosphor-icons/react/MapTrifold";
import { PencilSimple } from "@phosphor-icons/react/PencilSimple";
import { User } from "@phosphor-icons/react/User";
import { paths } from "config/paths";
import NextLink from "next/link";
import * as React from "react";
import { displayBytes, useCheckoutPricing } from "utils";
import {
	Account,
	AccountUsageStats,
	SUBSCRIPTION_CYCLE,
	SUBSCRIPTION_TYPE,
} from "~ui-components/types/__generated/gql/graphql";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { getRecurlyHostedBillingInfoUrl } from "~utils/recurly/get-recurly-hosted-billing-info-url";
import { PLAN_NAME, PLAN_OFFER } from "../edit-subscription";

export interface SummaryProps {
	account: Account;
	accountUsageStats: AccountUsageStats;
}

export const Summary: React.FC<SummaryProps> = ({
	account,
	accountUsageStats,
}) => {
	const planItems = usePlanItems(account, accountUsageStats);
	const userItems = useUserItems(account, accountUsageStats);

	return (
		<>
			<Card>
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(3, 1fr)",
						},
						p: 3,
					}}>
					{planItems.map((item, index) => (
						<Stack
							key={index}
							spacing={1}
							sx={{
								borderRight:
									index === planItems.length - 1
										? {}
										: {
												xs: "none",
												md: "1px solid var(--mui-palette-divider)",
											},
								borderBottom:
									index === planItems.length - 1
										? {}
										: {
												xs: "1px solid var(--mui-palette-divider)",
												md: "none",
											},
								pb: { xs: 2, md: 0 },
								pr: { xs: 0, md: 2 },
							}}>
							<Typography color="text.secondary">{item.title}</Typography>
							<Typography variant="h3">{item.value}</Typography>
							<Box color="text.secondary">{item.footer}</Box>
						</Stack>
					))}
				</Box>
			</Card>
			<Card>
				<Box
					sx={{
						display: "grid",
						gap: 2,
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(3, 1fr)",
						},
						p: 3,
					}}>
					{userItems.map((item, index) => (
						<Stack
							key={index}
							spacing={1}
							sx={{
								borderRight:
									index === userItems.length - 1
										? {}
										: {
												xs: "none",
												md: "1px solid var(--mui-palette-divider)",
											},
								borderBottom:
									index === userItems.length - 1
										? {}
										: {
												xs: "1px solid var(--mui-palette-divider)",
												md: "none",
											},
								pb: { xs: 2, md: 0 },
								pr: { xs: 0, md: 2 },
							}}>
							<Typography color="text.secondary">{item.title}</Typography>
							<Typography variant="h3">{item.value}</Typography>
							<Box color="text.secondary">{item.footer}</Box>
						</Stack>
					))}
				</Box>
			</Card>
		</>
	);
};

const BILLING_CYCLE_DESCRIPTION: Record<SUBSCRIPTION_CYCLE, string> = {
	MONTHLY: "month",
	ANNUAL: "year",
	TWO_YEARS: "2 years",
	THREE_YEARS: "3 years",
	FOUR_YEARS: "4 years",
	FIVE_YEARS: "5 years",
};

const usePlanItems = (
	account: Account,
	accountUsageStats: AccountUsageStats,
) => {
	const router = useWebRouter();

	const { pricing } = useCheckoutPricing(
		account.subscription.paidConfig,
		// @ts-ignore
		account._recurlyAccountInfo?.billingAddress,
	);

	const totalAllowedMaps =
		PLAN_OFFER[account.subscription.paidConfig.plan].maps +
		account.subscription.paidConfig.addons.maps;
	const totalAllowedStorage =
		PLAN_OFFER[account.subscription.paidConfig.plan].storage +
		account.subscription.paidConfig.addons.storage;

	return [
		{
			title: "Plan",
			value: PLAN_NAME[account.subscription.paidConfig.plan],
			footer: (
				<Stack
					spacing={1}
					direction="row"
					alignItems="center"
					justifyContent="space-between">
					{account.subscription.type === SUBSCRIPTION_TYPE.TRIAL && (
						<Button
							LinkComponent={NextLink}
							size="small"
							variant="outlined"
							href={paths.accountName.settings.editSubscription(
								router._query.accountName,
							)}>
							Upgrade to paid
						</Button>
					)}

					{account.subscription.type === SUBSCRIPTION_TYPE.PAID && (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1}>
							<CreditCard />
							<Typography variant="caption">
								{pricing.price?.currency?.symbol} {pricing.price?.now?.total}
								&nbsp;/&nbsp;
								{
									BILLING_CYCLE_DESCRIPTION[
										account.subscription.paidConfig.cycle
									]
								}
							</Typography>
							{pricing.loading && <CircularProgress size="16px" />}
							<Link
								component={NextLink}
								href={getRecurlyHostedBillingInfoUrl(
									account._recurlyAccountInfo?.hostedToken ?? "",
								)}
								target="_blank"
								color="unset"
								mt={0.5}>
								<PencilSimple />
							</Link>
						</Stack>
					)}
				</Stack>
			),
		},
		{
			title: "Maps",
			value: accountUsageStats.maps,
			footer: (
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<MapTrifold />
					<Typography variant="caption">of {totalAllowedMaps}</Typography>
				</Stack>
			),
		},
		{
			title: "Storage",
			value: displayBytes(accountUsageStats.storage),
			footer: (
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<HardDrives />
					<Typography variant="caption">of {totalAllowedStorage} GB</Typography>
				</Stack>
			),
		},
	];
};

const useUserItems = (
	account: Account,
	accountUsageStats: AccountUsageStats,
) => {
	const totalAllowedAdmins = account.subscription.paidConfig.addons.admins;
	const totalAllowedEditors = account.subscription.paidConfig.addons.editors;

	return [
		{
			title: "Administrators",
			value: accountUsageStats.admins,
			footer: (
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<User />
					<Typography variant="caption">of {totalAllowedAdmins}</Typography>
				</Stack>
			),
		},
		{
			title: "Editors",
			value: accountUsageStats.editors,
			footer: (
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<User />
					<Typography variant="caption">of {totalAllowedEditors}</Typography>
				</Stack>
			),
		},
		{
			title: "Viewers",
			value: accountUsageStats.viewers,
			footer: (
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}>
					<User />
					<Typography variant="caption">
						of
						{PLAN_OFFER[account.subscription.paidConfig.plan].isViewersUnlimited
							? " unlimited"
							: " 0"}
					</Typography>
				</Stack>
			),
		},
	];
};
